// src/components/SubmissionForm.tsx
import React, { useState } from 'react';
import { Participant } from '../data/eurovisionData';

interface SubmissionFormProps {
  rankingList: (Participant | null)[];
  onSubmit: (email: string, username: string) => void; // No country parameter
}

export function SubmissionForm({ rankingList, onSubmit }: SubmissionFormProps) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!email) {
      alert('Please enter your email address');
      return;
    }
    
    // Verify ranking is complete
    if (rankingList.some(item => item === null)) {
      alert('Please complete your ranking before submitting');
      return;
    }
    
    // Call the onSubmit callback with the form data (no country)
    onSubmit(email, username);
  };
  
  // Check if the ranking is fully filled (no null values)
  const isRankingComplete = !rankingList.some(item => item === null);
  
  // Count filled slots for button text
  const filledCount = rankingList.filter(item => item !== null).length;
  const totalSlots = rankingList.length;
  
  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-4">Submit your ranking!</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Email field */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <input
              type="email"
              className="bg-gray-100 pl-10 pr-3 py-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          {/* Username field */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="bg-gray-100 pl-10 pr-3 py-3 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Pick Your Eurovision Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          
          {/* Country field removed */}
          
          {/* Submit button - disabled unless ranking is complete */}
          <button
            type="submit"
            className={`w-full py-3 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-medium ${
              isRankingComplete 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!isRankingComplete}
          >
            {isRankingComplete 
              ? 'Submit' 
              : `Complete Your Ranking (${filledCount}/${totalSlots})`}
          </button>
        </div>
      </form>
    </div>
  );
}